import { useEffect, useState } from "react";
import "./EditSchool.css";
import TopImage from "../../common/TopImage";
import { Images } from "../../../../config/images";
import { DashboardStats } from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../common/CustomInput";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../../common/BackButton";
import { handleSmartBack } from "../../../../utils/navigationHelper";
import LicenseModal from "../../../common/LicenseModal";
import { useGetSubSchoolReportById } from "../../../../hooks/organization/report/useGetSubSchoolReportById";
import { useAssignLicense } from "../../../../hooks/organization/useAssignLicense";
import { useDisableLicenseForSubSchool } from "../../../../hooks/organization/useDisableLicenseForSubSchool";
import { useUpdateSubSchool } from "../../../../hooks/organization/school/useUpdateSubSchool";
import { useChangeSubSchoolStatus } from "../../../../hooks/organization/school/useChangeSubSchoolStatus";
import SuccessModal from "../../../common/SuccessModal";
import ChangeSchoolStatusModal from "./ChangeSchoolStatusModal";

const EditSchool = () => {
  const navigate = useNavigate();
  const { id: schoolId } = useParams();

  // Modal states
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseModalType, setLicenseModalType] = useState("add");

  // Fetch school data
  const {
    loading: schoolLoading,
    error: schoolError,
    reportData,
    getSubSchoolReportById,
  } = useGetSubSchoolReportById();

  // Assign license hook
  const {
    loading: assignLoading,
    error: assignError,
    assignLicense,
  } = useAssignLicense();

  // Disable license hook
  const {
    loading: disableLoading,
    error: disableError,
    disableLicense,
  } = useDisableLicenseForSubSchool();

  // Update sub-school hook
  const {
    updateSubSchool,
    loading: updateLoading,
    error: updateError,
  } = useUpdateSubSchool();

  // Change school status hook
  const {
    loading: statusLoading,
    error: statusError,
    changeSubSchoolStatus,
  } = useChangeSubSchoolStatus();

  // Status modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusAction, setStatusAction] = useState("delete");

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [newImage, setNewImage] = useState(null);

  // Fetch school data on mount
  useEffect(() => {
    if (schoolId) {
      getSubSchoolReportById(schoolId);
    }
  }, [schoolId]);

  // School data from API
  const schoolData = reportData?.school;
  const licensesAssigned = schoolData?.licenses_assigned ?? 0;
  const licensesUsed = schoolData?.licenses_used ?? 0;

  // Stats data from API
  const stats = [
    {
      label: "Total Licenses Assigned",
      value: licensesAssigned.toLocaleString(),
      icon: "fa-user",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Licenses Used",
      value: licensesUsed.toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Licenses Available",
      value: (licensesAssigned - licensesUsed).toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  const validationSchema = Yup.object({
    schoolName: Yup.string().required("School Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("School Email is required"),
    remarks: Yup.string(),
    image: Yup.mixed()
      .notRequired()
      .test(
        "fileSize",
        "Image must be less than 2MB",
        (value) => !value || value.size <= 2 * 1024 * 1024,
      )
      .test(
        "fileType",
        "Only PNG, JPG, and JPEG images are allowed",
        (value) =>
          !value ||
          ["image/png", "image/jpg", "image/jpeg"].includes(value.type),
      ),
  });

  const formik = useFormik({
    initialValues: {
      schoolName: schoolData?.name || "",
      email: schoolData?.email || "",
      assignedLicenses: licensesAssigned,
      usedLicenses: licensesUsed,
      remarks: "",
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const updateData = {
        name: values.schoolName,
        email: values.email,
      };
      if (newImage) {
        updateData.image = newImage;
      }
      const success = await updateSubSchool(schoolId, updateData);

      if (success) {
        setShowSuccessModal(true);
        setNewImage(null);
        getSubSchoolReportById(schoolId);
      }
    },
  });

  // Update form values when school data loads
  useEffect(() => {
    if (schoolData) {
      formik.setValues({
        ...formik.values,
        schoolName: schoolData.name || "",
        email: schoolData.email || "",
        assignedLicenses: licensesAssigned,
        usedLicenses: licensesUsed,
      });
    }
  }, [schoolData, licensesAssigned, licensesUsed]);

  const handleDisableSchool = () => {
    setStatusAction("delete");
    setShowStatusModal(true);
  };

  const handleEnableSchool = () => {
    setStatusAction("restore");
    setShowStatusModal(true);
  };

  const handleStatusSubmit = async (action, reason) => {
    return await changeSubSchoolStatus(schoolId, action, reason, () => {
      getSubSchoolReportById(schoolId);
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      formik.setFieldValue("image", file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setNewImage(null);
    formik.setFieldValue("image", null);
    setImagePreview(null);
  };

  const handleAddLicenses = () => {
    setLicenseModalType("add");
    setShowLicenseModal(true);
  };

  const handleDisableLicenses = () => {
    setLicenseModalType("disable");
    setShowLicenseModal(true);
  };

  const handleLicenseSubmit = async (data, onSuccess) => {
    if (licenseModalType === "add") {
      return await assignLicense(data, onSuccess);
    } else {
      return await disableLicense(data, onSuccess);
    }
  };

  const handleLicenseSuccess = () => {
    // Refresh school data after license operation
    getSubSchoolReportById(schoolId);
  };

  const isInitialLoad = reportData === undefined;

  return (
    <div className="edit-school-cnt">
      <BackButton />
      {/* Top Banner */}
      <TopImage text="SCHOOL" imageSrc={Images.TopImage} />

      {/* Stats Cards */}
      <div className="container-fluid p-0">
        {schoolLoading && isInitialLoad ? (
          <DashboardStatsSkeleton count={3} />
        ) : (
          <DashboardStats stats={stats} />
        )}
      </div>

      {/* Form Section */}
      <div className="edit-school-form-section">
        {/* Header with Disable Link */}
        <div className="school-header-row">
          <h2 className="school-edit-title">
            {schoolLoading && isInitialLoad
              ? "Loading..."
              : schoolData?.name || "School"}
          </h2>
          {!(schoolLoading && isInitialLoad) && (
            String(schoolData?.status) === "2" ? (
              <span className="disable-link text-success fw-bold" style={{ cursor: "pointer" }} onClick={handleEnableSchool}>
                +Enable School
              </span>
            ) : (
              <span className="disable-link" onClick={handleDisableSchool}>
                -Disable School
              </span>
            )
          )}
        </div>

        {schoolLoading && isInitialLoad ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <div className="row mb-5">
                <div className="col-md-6">
                  <CustomInput
                    label="School Name"
                    name="schoolName"
                    type="text"
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="School Email"
                    name="email"
                    type="email"
                  />
                </div>
              </div>

              {/* School Image */}
              <div className="row mb-5">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">School Image <span style={{ color: '#888', fontWeight: 'normal' }}>(Optional)</span></label>
                  <div
                    style={{
                      border: '2px dashed #c5c5c5',
                      borderRadius: '10px',
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: '#fafafa',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                    onClick={() => document.getElementById('edit-school-image-input').click()}
                  >
                    <input
                      id="edit-school-image-input"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageChange}
                    />
                    {(imagePreview || schoolData?.image_url) ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={imagePreview || schoolData?.image_url}
                          alt="School"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '150px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                          }}
                        />
                        {imagePreview && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage();
                            }}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: '#e74c3c',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        )}
                        <p style={{ margin: '8px 0 0', color: '#888', fontSize: '12px' }}>Click to change image</p>
                      </div>
                    ) : (
                      <div>
                        <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '28px', color: '#888', marginBottom: '8px' }}></i>
                        <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>Click to upload school image</p>
                        <p style={{ margin: 0, color: '#aaa', fontSize: '12px' }}>PNG, JPG, JPEG (Max 2MB)</p>
                      </div>
                    )}
                  </div>
                  {formik.errors.image && formik.touched.image && (
                    <div className="custom-error" style={{ marginTop: '6px' }}>{formik.errors.image}</div>
                  )}
                </div>
              </div>

              {/* License Allocation Section */}
              <div className="form-subheader-row">
                <h3 className="form-subheader">LICENSE ALLOCATION</h3>
                <div className="section-actions">
                  <span className="action-link add" onClick={handleAddLicenses}>
                    + Add Licenses
                  </span>
                  <span
                    className="action-link disable"
                    onClick={handleDisableLicenses}
                  >
                    -Disable Licenses
                  </span>
                </div>
              </div>
              <div className="separator" style={{ marginBottom: "25px" }} />

              <div className="row mb-4">
                <div className="col-md-6">
                  <CustomInput
                    label="Assigned Licenses"
                    name="assignedLicenses"
                    type="number"
                    disabled
                    style={{ backgroundColor: "#F8F9FA" }}
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Used Licenses"
                    name="usedLicenses"
                    type="number"
                    disabled
                    style={{ backgroundColor: "#F8F9FA", border: "2px solid #00AEEF" }}
                  />
                </div>
              </div>



              {/* Update Error */}
              {updateError && (
                <div className="custom-error mb-3">{updateError}</div>
              )}

              {/* Action Buttons */}
              <div className="actions-row">
                <Button
                  variant="outline-dark"
                  className="cancel-btn"
                  onClick={() => handleSmartBack(navigate, "/organization/school")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="save-btn"
                  disabled={!formik.isValid || formik.isSubmitting || updateLoading}
                >
                  {updateLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </FormikProvider>
        )}
      </div>

      {/* License Modal */}
      <LicenseModal
        showModal={showLicenseModal}
        handleClose={() => setShowLicenseModal(false)}
        type={licenseModalType}
        schoolName={schoolData?.name || ""}
        schoolId={parseInt(schoolId)}
        currentLicenses={licensesAssigned - licensesUsed}
        loading={licenseModalType === "add" ? assignLoading : disableLoading}
        error={licenseModalType === "add" ? assignError : disableError}
        onSubmit={handleLicenseSubmit}
        onSuccess={handleLicenseSuccess}
      />

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Success"
        message="School details have been updated successfully."
        primaryButtonText="Continue Editing"
        onPrimaryClick={() => setShowSuccessModal(false)}
        secondaryButtonText="School List"
        secondaryButtonLink="/organization/school"
      />

      {/* Change Status Modal */}
      <ChangeSchoolStatusModal
        showModal={showStatusModal}
        handleClose={() => setShowStatusModal(false)}
        schoolName={schoolData?.name || ""}
        action={statusAction}
        loading={statusLoading}
        error={statusError}
        onSubmit={handleStatusSubmit}
      />
    </div>
  );
};

export default EditSchool;
